import * as React from 'react';

import { CheckCircle, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Template } from '@klareospace/pharos-lib-types';
import { Button, Card, CardContent, CardHeader, EntityIcon } from '@klareospace/pharos-lib-web-utils';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CatalogCardProps {
  installLabel: string;
  installedLabel: string;
  isInstalled?: boolean;
  onInstall: (template: Template) => void;
  template: Template;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CatalogCard({
  installLabel,
  installedLabel,
  isInstalled = false,
  onInstall,
  template,
}: CatalogCardProps): React.JSX.Element {
  const { t } = useTranslation('settings');

  function handleInstallClick(): void {
    onInstall(template);
  }

  return (
    <Card className="flex flex-col overflow-hidden rounded-xl shadow-sm transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-start gap-3 pb-2 pt-4">
        <EntityIcon
          alt={template.name}
          fallbackText={template.code.slice(0, 2).toUpperCase()}
          size="md"
          src={template.logo || undefined}
        />
        <div className="min-w-0">
          <p className="truncate font-semibold leading-tight">{template.name}</p>
          <p className="text-xs text-muted-foreground">{template.code}</p>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4 pb-4">
        <p className="text-sm text-muted-foreground line-clamp-3">{template.description}</p>
        {isInstalled ? (
          <Button className="w-full" size="sm" variant="outline" disabled>
            <CheckCircle className="mr-2 h-4 w-4" />
            {t(installedLabel)}
          </Button>
        ) : (
          <Button
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handleInstallClick}
            size="sm"
          >
            <Download className="mr-2 h-4 w-4" />
            {t(installLabel)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
